import axios from "axios";
import { triggerSessionAlert } from "../utils/SessionAlertHelper";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper function to validate formData
const validateFormData = (formData) => {
  if (!formData || typeof formData !== "object") {
    return {
      success: false,
      message: "Invalid form data provided",
    };
  }
  if (!formData.filters || typeof formData.filters !== "object") {
    return {
      success: false,
      message: "Filters object is required in form data",
    };
  }
  return { success: true };
};

// Fixed transformFilters function
const transformFilters = (filters) => {
  const { destinationNumber, referral, hasReferral, ...otherFilters } = filters;
  
  const result = {
    ...otherFilters,
  };
  
  result.mobileNumber = result.mobileNumber || destinationNumber || "";
  result.referral = referral || "";
  
  if (hasReferral !== undefined) {
    result.hasReferral = hasReferral.toString();
  }
  
  return result;
};

// Rest of your API functions (unchanged)
export const getInternationalReloadData = async (formData) => {
  try {
    const validation = validateFormData(formData);
    if (!validation.success) {
      return validation;
    }

    const transformedFilters = transformFilters(formData.filters);

    console.log("Sending API request with filters:", JSON.stringify(transformedFilters, null, 2));

    const response = await axios.post(
      `${API_BASE_URL}/international-orders`,
      {
        ...formData,
        filters: transformedFilters,
      },
      {
        withCredentials: true,
      }
    );

    return {
      success: true,
      data: response.data.data.orders,
      totalPages: response.data.data.totalPages,
      currentPage: response.data.data.currentPage,
      totalCount: response.data.data.totalOrders,
      totalRevenue:
        response.data.data.totalSales?.$numberDecimal ||
        response.data.data.totalSales ||
        "0",
    };
  } catch (error) {
    // Error handling remains unchanged
    console.error("API error:", error.response || error.message);
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message: "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching international orders.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const getExportInternationalReloadData = async (filters, searchTerm) => {
  try {
    if (!filters || typeof filters !== "object") {
      return {
        success: false,
        message: "Filters object is required",
      };
    }

    const transformedFilters = transformFilters(filters);

    console.log("Export API request with filters:", JSON.stringify(transformedFilters, null, 2));

    const response = await axios.post(
      `${API_BASE_URL}/international-orders/export`,
      {
        filters: transformedFilters,
        search: searchTerm,
      },
      {
        withCredentials: true,
      }
    );

    console.log("Export API response:", JSON.stringify(response.data, null, 2));

    return {
      success: response.data.success,
      data: response.data.data.orders,
      totalCount: response.data.data.totalOrders,
    };
  } catch (error) {
    // Error handling remains unchanged
    console.error("Export API error:", error.response || error.message);
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message: "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while exporting international orders.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};


export const updateOrderStatus = async (orderId, newStatus, note = "") => {
  try {
    if (!orderId) {
      return {
        success: false,
        message: "Order ID is required to update payment status",
      };
    }
    if (!newStatus) {
      return {
        success: false,
        message: "New status is required to update payment status",
      };
    }
    if (!note) {
      return {
        success: false,
        message: "Note is required to update payment status",
      };
    }

    const response = await axios.patch(
      `${API_BASE_URL}/international-orders/order-status`,
      {
        orderId: orderId,
        orderStatus: newStatus,
        orderNote: note,
      },
      {
        withCredentials: true,
      }
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (error) {
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while updating international order status.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const updatePaymentStatus = async (orderId, newStatus, note = "") => {
  try {
    if (!orderId) {
      return {
        success: false,
        message: "Order ID is required to update payment status",
      };
    }
    if (!newStatus) {
      return {
        success: false,
        message: "New status is required to update payment status",
      };
    }
    if (!note) {
      return {
        success: false,
        message: "Note is required to update payment status",
      };
    }
    const response = await axios.patch(
      `${API_BASE_URL}/international-orders/payment-status`,
      {
        orderId: orderId,
        paymentStatus: newStatus,
        paymentNote: note,
      },
      {
        withCredentials: true,
      }
    );

    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data,
    };
  } catch (error) {
    if (!error.response) {
      window.location.href = "/server-down";
      return {
        success: false,
        message:
          "Cannot connect to the server. Please check your network or try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }
    if (error.response?.status === 401) {
      triggerSessionAlert("Your session has expired. Please sign in again.");
      return;
    }
    if (error.response?.status >= 500) {
      window.location.href = "/server-down";
      return;
    }
    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while updating international order payment status.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};

export const handleStatusUpdate = async (updateData) => {
  const { id, type, newStatus, note } = updateData;

  try {
    if (type === "order") {
      return await updateOrderStatus(id, newStatus, note);
    } else if (type === "payment") {
      return await updatePaymentStatus(id, newStatus, note);
    } else {
      throw new Error("Invalid update type specified");
    }
  } catch (error) {
    throw error;
  }
};



export const getReferralCodes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/referral/only`, {
      withCredentials: true,
    });

    return {
      success: response.data.success,
      message: response.data.message,
      referralCodes: response.data.data.referrals.map((ref) => ({
        id: ref.id,
        code: ref.code,
      })),
    };
  } catch (error) {
    if (!error.response) {
      return {
        success: false,
        message: "Cannot connect to the server. Please try again later.",
        error: {
          code: "NETWORK_ERROR",
          message: error.message || "Network Error",
          solution: "Ensure the backend server is running and reachable.",
        },
      };
    }

    return {
      success: false,
      message:
        error.response?.data?.message ||
        "An error occurred while fetching referral codes.",
      error: error.response?.data?.error || {
        code: error.response?.status || 500,
        message: error.message || "Internal Server Error",
        solution: "Please try again later",
      },
    };
  }
};